import { Icon, Accordion, toggleExpandedIndexes } from '@monash/portal-react';
import { useContext, useEffect, useState } from 'react';
import c from './link-form.module.scss';
import SlideOutForm from '../../common/slide-out/SlideOutForm';
import { SlideOutContext } from '../../common/slide-out/SlideOutWrapper';
import { FormProvider, useForm } from 'react-hook-form';
import { transformLinkDataForForm } from './utils/transform-link-data-for-form';
import PreviewLinkCard from './preview-link-card/PreviewLinkCard';
import Categories from './categories/Categories';
import Display from './display/Display';
import AdditionalSearchOptimisation from './additional-search-optimisation/AdditionalSearchOptimisation';
import Filters from './filters/Filters';
import {
  AccessibilityContext,
  APIContext,
  AuthContext,
} from '@monash/portal-frontend-common';
import * as Sentry from '@sentry/browser';
import ACCESS_ROLES from '../../../constants/user-roles';
import { isAuthorized } from '../../utilities/is-authorized';
import { SUBMIT_TYPES, LINK_STATUS } from '../shared/constants';
import { transformLinkForSubmission } from './utils/transform-link-for-submission';
import { useSnackbar } from '../../providers/SnackbarProvider';
import HistoryTimeline from '../../common/history-timeline/HistoryTimeline';
import { getErroredAccordionIndexes } from './utils/get-errored-accordion-indexes';
import SchedulePublishModal from '../../common/schedule-publish-modal/SchedulePublishModal';
import { getTimestampFromDateAndTimeInputs } from '../../utilities/get-timestamp-from-date-and-time-inputs';
import { getErrorFromApiErrorMsg } from '../../admin/notifications/notification-form/utils';
import { LINKS_CLIENT_ERROR_MSGS, LINKS_SERVER_ERROR_MSGS } from './constants';

const LinkForm = ({
  existing: existingProp,
  deleteLink: deleteLinkInUi,
  addLink: addLinkInUi,
  updateLink: updateLinkInUi,
}) => {
  const [existing, setExisting] = useState(existingProp);
  const { open, setOpen, halfWidth } = useContext(SlideOutContext);
  const { createLink, updateLink, deleteLink, deleteArchivedLink } =
    useContext(APIContext);
  const authCtx = useContext(AuthContext);
  const { resetAppLiveMsgs } = useContext(AccessibilityContext);
  const { addSnackbar } = useSnackbar();
  const addSnackbarMsg = (msg, msgType) => {
    resetAppLiveMsgs();
    addSnackbar({
      message: msg,
      type: msgType,
    });
  };

  const [openAccordionIndexes, setOpenAccordionIndexes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [inlineError, setInlineError] = useState(null);
  const isExisting = Boolean(existing);
  const isPublished = existing?.status === LINK_STATUS.PUBLISHED;
  const isScheduled = existing?.status === LINK_STATUS.SCHEDULED;
  const isArchived = existing?.status === LINK_STATUS.ARCHIVED;
  const isFormDisabled = Boolean(isSubmitting) || !open || isArchived;
  const isHeaderDisabled = Boolean(isSubmitting) || !open || !isExisting;

  const initialValues = transformLinkDataForForm(existing);

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: initialValues,
  });

  const {
    watch,
    handleSubmit,
    getValues,
    reset,
    formState: { isDirty },
  } = methods;

  const name = watch('name');

  // RHF docs recommend calling reset in useEffect after submission
  // https://react-hook-form.com/docs/useform/reset
  // This is to properly re-initialise default form values, b/c this component doesn't unmount
  useEffect(() => {
    const reinit = transformLinkDataForForm(existing);
    reset(reinit);
  }, [existing]);

  const closeOnSuccess = () => {
    setOpen(false);
    setInlineError(null);
  };

  const onDelete = async () => {
    try {
      // Deleting drafts/scheduled and archiving published links is done via the same endpoint
      // If they ever diverge, can create separate onDelete and onArchive handlers
      const resp = await deleteLink(existing.id);
      deleteLinkInUi(existing.id);
      addSnackbarMsg(
        `Link has been ${isPublished ? 'archived' : 'deleted'}`,
        'success'
      );
      closeOnSuccess();
      return resp;
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);

      // throwing an error instead of handling as it is done in action modal
      throw error;
    }
  };

  const onDeleteArchived = async () => {
    try {
      const resp = await deleteArchivedLink(existing.id);
      deleteLinkInUi(existing.id);
      addSnackbarMsg(`Link has been deleted`, 'success');
      closeOnSuccess();
      return resp;
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);

      // throwing an error instead of handling as it is done in action modal
      throw error;
    }
  };

  const onCopyFromArchive = async () => {
    try {
      const submitData = { ...existing, startDate: null, categories: [] };
      const resp = await createLink(submitData);
      addSnackbarMsg('Link has been saved', 'success');
      closeOnSuccess();
      return resp;
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);

      // throwing an error instead of handling as it is done in action modal
      throw error;
    }
  };

  const getFormHeaderActions = () => {
    const ACTION = {
      HISTORY: {
        icon: Icon.ClockReverse,
        onClick: () => {},
        label: 'View history',
        disabled: isHeaderDisabled,
        modalProps: {
          title: 'History',
          content: <HistoryTimeline history={existing?.history} />,
        },
      },
      DELETE: {
        icon: Icon.Trash,
        label: 'Delete link',
        disabled: isHeaderDisabled,
        modalProps: {
          title: 'Delete link',
          content: 'Are you sure you want to delete this link?',
          cta: {
            async: true,
            onClick: onDelete,
            onError: (error) =>
              getErrorFromApiErrorMsg(
                error.message,
                LINKS_SERVER_ERROR_MSGS.DELETE,
                LINKS_CLIENT_ERROR_MSGS.DELETE
              ),
            variant: 'delete',
            label: 'Delete',
          },
        },
      },
      ARCHIVE: {
        icon: Icon.Box,
        label: 'Archive link',
        disabled: isHeaderDisabled,
        modalProps: {
          title: 'Archive link',
          content:
            'Archive will remove the link from the student app. Are you sure you want to archive this link?',
          cta: {
            async: true,
            onClick: onDelete,
            onError: (error) =>
              getErrorFromApiErrorMsg(
                error.message,
                LINKS_SERVER_ERROR_MSGS.ARCHIVE,
                LINKS_CLIENT_ERROR_MSGS.ARCHIVE
              ),
            variant: 'delete',
            label: 'Archive',
          },
        },
      },
      DELETE_ARCHIVED: {
        icon: Icon.Trash,
        label: 'Delete link',
        disabled: isHeaderDisabled,
        modalProps: {
          title: 'Delete link',
          content: 'Are you sure you want to delete this link?',
          cta: {
            async: true,
            onClick: onDeleteArchived,
            onError: (error) =>
              getErrorFromApiErrorMsg(
                error.message,
                LINKS_SERVER_ERROR_MSGS.DELETE,
                LINKS_CLIENT_ERROR_MSGS.DELETE
              ),
            variant: 'delete',
            label: 'Delete',
          },
        },
      },
      COPY: {
        icon: Icon.Copy,
        label: 'Copy and create a new draft',
        disabled: isHeaderDisabled,
        modalProps: {
          title: 'Create new draft from archive',
          content: `Are you sure you want to make a draft copy from ${existing?.name}, without the original history?`,
          cta: {
            async: true,
            onClick: onCopyFromArchive,
            onError: (error) =>
              getErrorFromApiErrorMsg(
                error.message,
                LINKS_SERVER_ERROR_MSGS.DRAFT,
                LINKS_CLIENT_ERROR_MSGS.DRAFT
              ),
            variant: 'primary',
            label: 'Create',
          },
        },
      },
    };

    if (isArchived) {
      return [ACTION.HISTORY, ACTION.COPY, ACTION.DELETE_ARCHIVED];
    }

    if (isPublished) {
      return [ACTION.HISTORY, ACTION.ARCHIVE];
    }

    return [ACTION.HISTORY, ACTION.DELETE];
  };

  const onSaveDraft = async (data) => {
    try {
      setIsSubmitting(SUBMIT_TYPES.DRAFT);
      const coreData = transformLinkForSubmission(data);
      const submitData = { ...coreData, startDate: null };

      if (isExisting) {
        const updatedLink = await updateLink(submitData);
        setExisting(updatedLink);
        updateLinkInUi(updatedLink);
      } else {
        const newLink = await createLink(submitData);
        addLinkInUi(newLink);
      }

      addSnackbarMsg('Link has been saved', 'success');
      closeOnSuccess();
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      setInlineError(
        getErrorFromApiErrorMsg(
          error.message,
          LINKS_SERVER_ERROR_MSGS.DRAFT,
          LINKS_CLIENT_ERROR_MSGS.DRAFT
        )
      );
    } finally {
      setIsSubmitting(null);
    }
  };

  const onPublish = async (data) => {
    try {
      setIsSubmitting(SUBMIT_TYPES.PUBLISH);
      const coreData = transformLinkForSubmission(data);
      const submitData = { ...coreData, startDate: null };

      if (isExisting) {
        const updatedLink = await updateLink(submitData, true);
        setExisting(updatedLink);
        updateLinkInUi(updatedLink);
      } else {
        const newLink = await createLink(submitData, true);
        addLinkInUi(newLink);
      }

      addSnackbarMsg('Link has been published', 'success');
      closeOnSuccess();
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      setInlineError(
        getErrorFromApiErrorMsg(
          error.message,
          LINKS_SERVER_ERROR_MSGS.PUBLISH,
          LINKS_CLIENT_ERROR_MSGS.PUBLISH
        )
      );
    } finally {
      setIsSubmitting(null);
    }
  };

  const openSchedulePublishModal = () => {
    setIsScheduleModalOpen(true);
  };

  const onSchedulePublish = async (data) => {
    try {
      setIsSubmitting(SUBMIT_TYPES.SCHEDULE);
      const coreData = transformLinkForSubmission(data);
      const submitData = {
        ...coreData,
        startDate: getTimestampFromDateAndTimeInputs(
          data.startDate,
          data.startTime
        ),
      };

      if (isExisting) {
        const updatedLink = await updateLink(submitData);
        setExisting(updatedLink);
        updateLinkInUi(updatedLink);
      } else {
        const newLink = await createLink(submitData);
        addLinkInUi(newLink);
      }

      addSnackbarMsg('Link has been scheduled', 'success');
      closeOnSuccess();
      setIsScheduleModalOpen(false);
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      setInlineError(
        getErrorFromApiErrorMsg(
          error.message,
          LINKS_SERVER_ERROR_MSGS.DRAFT,
          LINKS_CLIENT_ERROR_MSGS.DRAFT
        )
      );
    } finally {
      setIsSubmitting(null);
    }
  };

  const onRemoveSchedule = async () => {
    try {
      setIsSubmitting(SUBMIT_TYPES.REMOVE_SCHEDULED);

      // Remove start date from existing link, NOT taking into account other form inputs
      const submitData = {
        ...existing,
        startDate: null,
      };

      const updatedLink = await updateLink(submitData);
      setExisting(updatedLink);
      updateLinkInUi(updatedLink);
      addSnackbarMsg('Link has been saved', 'success');
      setIsScheduleModalOpen(false);
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      setInlineError(
        getErrorFromApiErrorMsg(
          error.message,
          LINKS_SERVER_ERROR_MSGS.DRAFT,
          LINKS_CLIENT_ERROR_MSGS.DRAFT
        )
      );
    } finally {
      setIsSubmitting(null);
    }
  };

  const onSave = async (data) => {
    try {
      setIsSubmitting(SUBMIT_TYPES.SAVE);
      const coreData = transformLinkForSubmission(data);
      const submitData = {
        ...coreData,
        startDate: getTimestampFromDateAndTimeInputs(
          data.startDate,
          data.startTime
        ),
      };

      const updatedLink = await updateLink(submitData);
      setExisting(updatedLink);
      updateLinkInUi(updatedLink);
      addSnackbarMsg('Link has been saved', 'success');
      closeOnSuccess();
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      setInlineError(
        getErrorFromApiErrorMsg(
          error.message,
          LINKS_SERVER_ERROR_MSGS.DRAFT,
          LINKS_CLIENT_ERROR_MSGS.DRAFT
        )
      );
    } finally {
      setIsSubmitting(null);
    }
  };

  const onInvalidFormError = (errors) => {
    // Open accordion sections where there are errors
    const erroredAccordionIndexes = getErroredAccordionIndexes({
      errors,
    });
    setOpenAccordionIndexes(erroredAccordionIndexes);
  };

  const { SUPPORT_DEVELOPER, LINKS_ADMIN, LINKS_DRAFT } = ACCESS_ROLES;

  const formSubmitActions = [
    {
      label: 'Publish',
      onClick: handleSubmit(
        async (data) => await onPublish(data),
        onInvalidFormError
      ),
      loading: isSubmitting === SUBMIT_TYPES.PUBLISH,
      disabled: isFormDisabled,
      requiredRoles: [SUPPORT_DEVELOPER, LINKS_ADMIN],
      showWhenPublished: true,
    },
    isScheduled
      ? {
          label: 'Save',
          onClick: handleSubmit(
            async (data) => await onSave(data),
            onInvalidFormError
          ),
          loading: isSubmitting === SUBMIT_TYPES.SAVE,
          disabled: isFormDisabled,
          requiredRoles: [SUPPORT_DEVELOPER, LINKS_ADMIN],
        }
      : {
          label: 'Save as draft',
          onClick: () => onSaveDraft(getValues()), // Manually get form values instead of calling handleSubmit, because we don't want RHF to validate drafts
          loading: isSubmitting === SUBMIT_TYPES.DRAFT,
          disabled: isFormDisabled,
          requiredRoles: [SUPPORT_DEVELOPER, LINKS_ADMIN, LINKS_DRAFT],
        },
    {
      label: 'Schedule publish',
      onClick: handleSubmit(openSchedulePublishModal, onInvalidFormError),
      disabled: isFormDisabled,
      requiredRoles: [SUPPORT_DEVELOPER, LINKS_ADMIN],
    },
  ]
    .filter((action) =>
      isAuthorized(authCtx?.user?.roles, action.requiredRoles)
    )
    .filter((action) => {
      const isAvailable = !isExisting || !isPublished;
      return isAvailable || action.showWhenPublished;
    });

  return (
    <FormProvider {...methods}>
      <SlideOutForm
        name="link"
        width={halfWidth}
        heading={name}
        headerActions={getFormHeaderActions()}
        submitActions={formSubmitActions}
        isDirty={isDirty}
        status={existing?.status}
        onClose={() => reset()}
        publishDate={isScheduled && existing?.startDate}
        submitError={inlineError}
      >
        <div className={c.previewContainer}>
          <PreviewLinkCard />
        </div>
        <Accordion
          useCustomStyles
          className={c.linkFormAccordion}
          stylesheet={c}
          renderHiddenContent
          expandedIndexes={openAccordionIndexes}
          onClick={(index) =>
            setOpenAccordionIndexes(
              toggleExpandedIndexes(index, openAccordionIndexes)
            )
          }
          items={[
            {
              title: 'Display',
              content: <Display disabled={isFormDisabled} />,
            },
            {
              title: 'Categories',
              content: <Categories disabled={isFormDisabled} />,
            },
            {
              title: 'Filters',
              content: <Filters disabled={isFormDisabled} />,
            },
            {
              title: 'Additional search optimisation',
              content: (
                <AdditionalSearchOptimisation disabled={isFormDisabled} />
              ),
            },
          ]}
          hasExpandAll={false}
        />
        <SchedulePublishModal
          title="Schedule publish link"
          isOpen={isScheduleModalOpen}
          onClose={() => setIsScheduleModalOpen(false)}
          dateFieldName="startDate"
          timeFieldName="startTime"
          onPublish={handleSubmit(
            async (data) => await onSchedulePublish(data)
          )}
          onRemoveSchedule={onRemoveSchedule}
          isScheduled={isScheduled}
          isScheduling={isSubmitting === SUBMIT_TYPES.SCHEDULE}
          isRemoving={isSubmitting === SUBMIT_TYPES.REMOVE_SCHEDULED}
          disabled={isFormDisabled}
          submitError={inlineError}
        />
      </SlideOutForm>
    </FormProvider>
  );
};

export default LinkForm;
